import { useMemo } from "react";
import { compact } from "lodash";
import { useFormContext } from "react-hook-form";
import { Checkout } from "@/react/components/Paywalls/Checkout";
import { BigLoader } from "@/react/components/shared/BigLoader";
import { StyledFrame } from "@/react/components/shared/StyledFrame";
import { usePunditUserContext } from "@/react/contexts";
import { usePaywallCouponList } from "@/react/hooks/paywalls/usePaywallCouponList";
import { PAYWALL_STATUS } from "@/react/types/Paywall";
import type { Paywall } from "@/react/types/Paywall";
import type { PaywallCoupon } from "@/react/types/PaywallCoupon";
import type { PaywallPrice } from "@/react/types/PaywallPrice";
import type { PaywallForm } from "../../../types/PaywallForm";
import { usePaywallFormContext } from "../../PaywallFormProvider";
import { generatePriceWithBackendVariables } from "../helpers";

const getCouponCode = (checkoutPath: string | undefined): string | null => {
  const queryParams = checkoutPath?.split("?")[1];
  const searchParams = new URLSearchParams(queryParams);
  return searchParams.get("coupon_code");
};

const emptyPrice: any = {
  price_type: "onetime",
  amount: 10000,
  trial_requires_upfront_payment: false,
  preferred: false,
  currency_id: 1,
  tmp_id: "non_existent",
};

export const CheckoutPreview = () => {
  const { selectedCurrency, checkoutDataQuery } = usePaywallFormContext();
  const { watch } = useFormContext<PaywallForm>();
  const paywall = watch();

  const forbidNavigationThroughTab = (node: HTMLElement | null) =>
    node && node.setAttribute("inert", "");

  const { currentCommunity, currentCommunityMember, currentUser } =
    usePunditUserContext();

  const { paywallCouponsListQuery } = usePaywallCouponList({
    paywallId: paywall?.id,
    enabled: true,
  });

  const couponFromCheckoutPath = getCouponCode(paywall?.checkout_path);

  const couponApplied: PaywallCoupon | undefined = couponFromCheckoutPath
    ? paywallCouponsListQuery?.data?.find(
        (coupon: PaywallCoupon) => coupon.code === couponFromCheckoutPath,
      )
    : undefined;

  const newPaywall = useMemo<Paywall | null>(() => {
    if (!selectedCurrency) {
      return null;
    }

    const prices = compact(
      paywall?.stripe_prices_attributes?.map(
        (price: PaywallPrice & { _destroy?: boolean }) =>
          generatePriceWithBackendVariables(price, paywall),
      ),
    );

    return {
      ...paywall,
      status: PAYWALL_STATUS.ACTIVE,
      currency: selectedCurrency,
      prices:
        prices.length === 0
          ? compact([generatePriceWithBackendVariables(emptyPrice, paywall)])
          : prices,
      paywall_customization: paywall?.paywall_customization_attributes,
    };
  }, [paywall, selectedCurrency]);

  if (
    !currentCommunity ||
    !currentCommunityMember ||
    !currentUser ||
    !checkoutDataQuery ||
    checkoutDataQuery?.isLoading ||
    !checkoutDataQuery?.data ||
    !newPaywall ||
    !newPaywall.prices ||
    !newPaywall.currency ||
    !selectedCurrency
  ) {
    return (
      <div className="bg-primary border-primary flex size-full flex-col items-center justify-center rounded-2xl border">
        <div>
          <BigLoader />
        </div>
      </div>
    );
  }

  const {
    community,
    community_payment_processor,
    community_member_payment_processor,
    policies,
    settings,
    auth_provider,
  } = checkoutDataQuery?.data || {};

  return (
    <StyledFrame
      data-testid="checkout-builder-frame"
      className="border-primary rounded-2xl border"
    >
      <div
        className="pointer-events-none size-full"
        ref={forbidNavigationThroughTab}
        data-testid="checkout-preview"
      >
        <Checkout
          paywall={newPaywall}
          currentCommunity={community}
          currentCommunityProcessorPaymentMethodConfigurationId={
            community_payment_processor.payment_method_configuration_id
          }
          isLinkCapabilityActive={
            community_payment_processor.is_link_capability_active
          }
          isMemberWithSavedPaymentMethods={
            community_member_payment_processor.is_member_with_saved_payment_methods
          }
          currentCommunityMemberPaymentProcessorSessionClientSecret={undefined}
          hasAlreadyPurchased={policies.has_already_purchased}
          loginUrl={settings.login_url}
          communityRootUrl={settings.community_url}
          checkoutConfirmationUrl={settings.checkout_confirmation_url}
          processorId={community_payment_processor.processor_id}
          processorKey={community_payment_processor.public_api_key}
          areCouponsEnabled={policies.are_coupons_enabled}
          isTaxCollectionEnabled={false}
          isMemberAddressComplete={false}
          isMemberAddressRequired={false}
          memberBillingInfo={undefined}
          isPaywallsEuPaymentMethodsEnabled={false}
          isBrazilianAccountAndProcessorStripeBr={
            community_payment_processor.is_brazilian_account_and_processor_stripe_brazil
          }
          shouldShowTaxIdForm={policies.should_show_tax_id_form}
          isTaxIdMandatory={community_payment_processor.is_tax_id_mandatory}
          authProviderProperties={auth_provider}
          shouldUseWrapper={false}
          resetPasswordUrl={settings.reset_password_url}
          checkoutPreviewContext={{
            community_id: currentCommunity.id,
            coupon_applied: couponApplied,
          }}
        />
      </div>
    </StyledFrame>
  );
};
